"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { supabase } from "../../supabaseClient"
import { useAuth } from "../../context/AuthContext"
import { applyInstitutionFilter } from "../../lib/tenant"
import { jsPDF } from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"
import "../../styles/HorarioList.css"

const DIAS_SEMANA_ORDEM = ["seg", "ter", "qua", "qui", "sex"]
const DIAS_SEMANA_NOMES = {
  seg: "SEGUNDA-FEIRA",
  ter: "TERÇA-FEIRA",
  qua: "QUARTA-FEIRA",
  qui: "QUINTA-FEIRA",
  sex: "SEXTA-FEIRA",
}

export default function HorarioList({ keyProp, professorId, turmaId, cursoId }) {
  const { isAdmin, instituicaoId } = useAuth()
  const [horarios, setHorarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [turmaInfo, setTurmaInfo] = useState(null)
  const [config, setConfig] = useState(null)
  const [todasTurmas, setTodasTurmas] = useState([])
  const [todosHorarios, setTodosHorarios] = useState([])
  const [todosProfessores, setTodosProfessores] = useState([])
  const [professorInfo, setProfessorInfo] = useState(null)
  const tableRef = useRef(null)

  useEffect(() => {
    const carregarConfig = async () => {
      let query = supabase.from("configuracoes").select("*")
      query = applyInstitutionFilter(query, isAdmin, instituicaoId)
      const { data } = await query.limit(1).maybeSingle()
      setConfig(data)
    }
    carregarConfig()
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    if (professorId && todosProfessores.length > 0) {
      const prof = todosProfessores.find(p => p.id === professorId)
      setProfessorInfo(prof || null)
    } else {
      setProfessorInfo(null)
    }
  }, [professorId, todosProfessores])

  const fetchHorarios = useCallback(async () => {
    try {
      setLoading(true)
      console.debug("fetchHorarios called", { isAdmin, instituicaoId, professorId, turmaId, cursoId })
      let query = supabase
        .from("horarios")
        .select(`
          id, hora_inicio, hora_fim, dia_semana,
          professor:professor_id ( id, nome ),
          disciplina:disciplina_id ( id, nome, codigo, tipo ),
          turma:turma_id (
            id, nome, periodo, classe, ano, turno, curso_id,
            sala:sala_id ( id, nome ),
            curso:curso_id(id, nome)
          )
        `)
        .order("hora_inicio", { ascending: true })

      query = applyInstitutionFilter(query, isAdmin, instituicaoId)

      if (professorId) query = query.eq("professor_id", professorId)
      if (turmaId) query = query.eq("turma_id", turmaId)

      const { data, error } = await query
      console.debug("raw horarios response", { data, error })
      if (error) throw error

      let filteredData = data || []
      if (cursoId) {
        filteredData = filteredData.filter((h) => h.turma?.curso_id === cursoId)
      }

      setTodosHorarios(data || [])

      let turmasQuery = supabase
        .from("turmas")
        .select("*, sala:sala_id(id, nome), curso:curso_id(id, nome)")
        .order("nome")
      turmasQuery = applyInstitutionFilter(turmasQuery, isAdmin, instituicaoId)
      const { data: turmasData } = await turmasQuery
      setTodasTurmas(turmasData || [])

      let profsQuery = supabase.from("professores").select("*").order("nome")
      profsQuery = applyInstitutionFilter(profsQuery, isAdmin, instituicaoId)
      const { data: professoresData, error: profsError } = await profsQuery
      console.debug("raw professores for horarios", { data: professoresData, error: profsError })
      setTodosProfessores(professoresData || [])

      if (filteredData?.length > 0) {
        if (turmaId) {
          setTurmaInfo(filteredData[0].turma)
        }
      }

      setHorarios(filteredData)
    } catch (err) {
      console.error("Erro:", err.message)
    } finally {
      setLoading(false)
    }
  }, [isAdmin, instituicaoId, professorId, turmaId, cursoId])

  useEffect(() => {
    fetchHorarios()
    const channel = supabase.channel("realtime-horarios")
    channel.on("postgres_changes", { event: "*", schema: "public", table: "horarios" }, fetchHorarios)
    channel.subscribe()
    return () => supabase.removeChannel(channel)
  }, [keyProp, professorId, turmaId, cursoId, fetchHorarios])

  if (loading) return <p className="loading-text">Carregando horários...</p>

  const organizarHorariosPorGrade = (horariosData = horarios) => {
    const temposUnicos = [...new Set(horariosData.map((h) => h.hora_inicio))].sort((a, b) => {
      const [ha, ma] = a.split(":").map(Number)
      const [hb, mb] = b.split(":").map(Number)
      return ha * 60 + ma - (hb * 60 + mb)
    })

    const grade = {}
    temposUnicos.forEach((tempo) => {
      grade[tempo] = {}
      DIAS_SEMANA_ORDEM.forEach((dia) => {
        grade[tempo][dia] = horariosData.find((h) => h.hora_inicio === tempo && h.dia_semana === dia) || null
      })
    })

    return { grade, temposUnicos }
  }

  const getDisciplinasComProfessores = (horariosData = horarios) => {
    const mapa = new Map()
    horariosData.forEach((h) => {
      if (h.disciplina?.nome) {
        const chave = `${h.disciplina.nome}-${h.professor?.nome || "Sem professor"}`
        if (!mapa.has(chave)) {
          mapa.set(chave, {
            disciplina: h.disciplina.nome,
            tipo: h.disciplina.tipo || "G",
            professor: h.professor?.nome || "Não definido",
          })
        }
      }
    })
    return Array.from(mapa.values())
  }

  // Converter hex para RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 107, 182]; // Default
  }

  const desenharCabecalhoInstituicao = (doc, configData, yStart = 15) => {
    const pageWidth = doc.internal.pageSize.getWidth()
    let yPos = yStart

    if (configData?.logo_url) {
      try {
        const logoWidth = 80
        const logoHeight = 80
        const logoX = (pageWidth - logoWidth) / 2
        doc.addImage(configData.logo_url, "PNG", logoX, yPos, logoWidth, logoHeight)
        yPos += logoHeight + 8
      } catch (e) {
        console.log("Erro ao adicionar logo:", e)
      }
    }

    const textX = pageWidth / 2

    if (configData?.cabecalho_linha1) {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(9)
      doc.setTextColor(0, 0, 0)
      doc.text(configData.cabecalho_linha1.toUpperCase(), textX, yPos, { align: "center" })
      yPos += 10
    }

    if (configData?.cabecalho_linha2) {
      doc.setFontSize(9)
      doc.text(configData.cabecalho_linha2.toUpperCase(), textX, yPos, { align: "center" })
      yPos += 10
    }

    if (configData?.cabecalho_linha3) {
      doc.setFont("helvetica", "bold")
      doc.setFontSize(11)
      doc.text(configData.cabecalho_linha3.toUpperCase(), textX, yPos, { align: "center" })
      yPos += 11
    }

    if (configData?.cabecalho_linha4) {
      doc.setFont("helvetica", "normal")
      doc.setFontSize(9)
      doc.text(configData.cabecalho_linha4.toUpperCase(), textX, yPos, { align: "center" })
      yPos += 10
    }

    yPos += 5

    return yPos
  }

  const gerarPDFTurma = (doc, turma, horariosT, configData, isFirstPage = false) => {
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 30;
  

    if (!isFirstPage) doc.addPage("landscape")

    let yPos = desenharCabecalhoInstituicao(doc, configData, 15)

    const semestre = configData?.semestre_atual || " "
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.text(`HORÁRIO DA TURMA "${turma?.nome || "A"}" - ${semestre}`, pageWidth / 2, yPos, { align: "center" })
    yPos += 12

    if (turma?.curso?.nome) {
      doc.setFontSize(10)
      doc.text(`CURSO DE ${turma.curso.nome.toUpperCase()}`, pageWidth / 2, yPos, { align: "center" })
      yPos += 10
    }

    doc.setFont("helvetica", "italic")
    doc.setFontSize(9)
    doc.text(`" PERÍODO ${turma?.periodo?.toUpperCase() || "REGULAR"} "`, pageWidth / 2, yPos, { align: "center" })
    yPos += 12

    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    doc.text(`${turma?.ano || "1"}º ANO/ SALA - ${turma?.sala?.nome || "01"}`, pageWidth - margin, yPos, {
      align: "right",
    })
    yPos += 10

    const { grade, temposUnicos } = organizarHorariosPorGrade(horariosT)

    const tableHeaders = [
      { content: "HORAS", styles: { halign: "center", fontStyle: "bold", cellWidth: 50, fontSize: 6 } },
      { content: "TEMPOS", styles: { halign: "center", fontStyle: "bold", cellWidth: 30, fontSize: 6 } },
      ...DIAS_SEMANA_ORDEM.map((dia) => ({
        content: DIAS_SEMANA_NOMES[dia],
        styles: { halign: "center", fontStyle: "bold", fontSize: 8 },
      })),
    ]

    const tableBody =
      temposUnicos.length > 0
        ? temposUnicos.map((tempo, idx) => {
            const horarioRef = horariosT.find((h) => h.hora_inicio === tempo)
            return [
              { content: `${tempo} - ${horarioRef?.hora_fim || ""}`, styles: { halign: "center", fontSize: 10 } },
              { content: `${idx + 1}º`, styles: { halign: "center", fontStyle: "bold", fontSize: 10 } },
              ...DIAS_SEMANA_ORDEM.map((dia) => ({
                content: grade[tempo][dia]?.disciplina?.nome || "-",
                styles: { halign: "center", fontSize: 10 },
              })),
            ]
          })
        : [[{ content: "-", colSpan: 7, styles: { halign: "center" } }]]

    const estiloTabela = configData?.estilo_tabela || "listrado"
    const isListrado = estiloTabela === "listrado"

    autoTable(doc, {
      startY: yPos,
      head: [tableHeaders],
      body: tableBody,
      theme: "grid",
      headStyles: { 
        fillColor: hexToRgb(configData?.cor_cabecalho || "#1e3a5f"), 
        textColor: hexToRgb(configData?.cor_texto || "#ffffff"), 
        fontSize: 8, 
        cellPadding: 3,
        fontStyle: "bold"
      },
      bodyStyles: { 
        fillColor: hexToRgb(configData?.cor_corpo || "#ffffff"), 
        textColor: hexToRgb(configData?.cor_texto || "#000000"), 
        fontSize: 8, 
        cellPadding: 2, 
        minCellHeight: 14 
      },
      ...(isListrado ? { alternateRowStyles: { fillColor: hexToRgb(configData?.cor_linha || "#e6ebff") } } : {}),
      margin: { left: margin, right: margin },
    })

    let finalY = doc.lastAutoTable.finalY + 15

    const disciplinasProfs = getDisciplinasComProfessores(horariosT)
    if (disciplinasProfs.length > 0) {
      autoTable(doc, {
        startY: finalY,
        head: [
          [
            { content: "DISCIPLINAS", styles: { halign: "left", fontSize: 6 } },
            { content: "NOME DO DOCENTE", styles: { halign: "left", fontSize: 6 } },
          ],
        ],
        body: disciplinasProfs.map((item) => [
          { content: `${item.disciplina} (${item.tipo})`, styles: { fontSize: 6 } },
          { content: item.professor, styles: { fontSize: 6 } },
        ]),
        theme: "grid",
        headStyles: { 
          fillColor: hexToRgb(configData?.cor_cabecalho || "#1e3a5f"), 
          textColor: hexToRgb(configData?.cor_texto || "#ffffff"), 
          fontSize: 6 
        },
        bodyStyles: { fontSize: 6, textColor: hexToRgb(configData?.cor_texto || "#000000") },
        margin: { left: margin, right: margin },
      })
      finalY = doc.lastAutoTable.finalY + 8
    }

    doc.setFont("helvetica", "normal")
    doc.setFontSize(7)
    doc.text("Legenda: (N) Nuclear (C) Complementar (G) Gerais", margin, finalY)

    finalY += 15
    doc.setFont("helvetica", "italic")
    doc.setFontSize(8)
    doc.setTextColor(100, 100, 100)
    doc.text("CAJÚ TIMEPLANNER | copyright 2026 ", pageWidth / 2, pageHeight - 20, { align: "center" })

    
  }

  const gerarPDFProfessor = (doc, professor, horariosP, configData, isFirstPage = false) => {
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 20

    if (!isFirstPage) doc.addPage("landscape")

    let yPos = desenharCabecalhoInstituicao(doc, configData, 15)

    const semestre = configData?.semestre_atual || " "
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.text(`HORÁRIO DO PROFESSOR - ${semestre}`, pageWidth / 2, yPos, { align: "center" })
    yPos += 14

    doc.setFontSize(11)
    doc.text(`Professor(a): ${professor.nome}`, pageWidth / 2, yPos, { align: "center" })
    yPos += 12

    const { grade, temposUnicos } = organizarHorariosPorGrade(horariosP)

    const tableHeaders = [
      { content: "HORAS", styles: { halign: "center", fontStyle: "bold", cellWidth: 50, fontSize: 7 } },
      { content: "TEMPOS", styles: { halign: "center", fontStyle: "bold", cellWidth: 30, fontSize: 7 } },
      ...DIAS_SEMANA_ORDEM.map((dia) => ({
        content: DIAS_SEMANA_NOMES[dia],
        styles: { halign: "center", fontStyle: "bold", fontSize: 7 },
      })),
    ]

    const tableBody =
      temposUnicos.length > 0
        ? temposUnicos.map((tempo, idx) => {
            const horarioRef = horariosP.find((h) => h.hora_inicio === tempo)
            return [
              { content: `${tempo} - ${horarioRef?.hora_fim || ""}`, styles: { halign: "center", fontSize: 6 } },
              { content: `${idx + 1}º`, styles: { halign: "center", fontStyle: "bold", fontSize: 7 } },
              ...DIAS_SEMANA_ORDEM.map((dia) => {
                const h = grade[tempo][dia]
                return {
                  content: h ? `${h.disciplina?.nome || ""}\n${h.turma?.nome || ""}` : "-",
                  styles: { halign: "center", fontSize: 6 },
                }
              }),
            ]
          })
        : [[{ content: "-", colSpan: 7, styles: { halign: "center" } }]]

    const estiloTabela = configData?.estilo_tabela || "listrado"
    const isListrado = estiloTabela === "listrado"

    autoTable(doc, {
      startY: yPos,
      head: [tableHeaders],
      body: tableBody,
      theme: "grid",
      headStyles: { 
        fillColor: hexToRgb(configData?.cor_cabecalho || "#1e3a5f"), 
        textColor: hexToRgb(configData?.cor_texto || "#ffffff"), 
        fontSize: 7, 
        cellPadding: 3,
        fontStyle: "bold"
      },
      bodyStyles: { 
        fillColor: hexToRgb(configData?.cor_corpo || "#ffffff"), 
        textColor: hexToRgb(configData?.cor_texto || "#000000"), 
        fontSize: 6, 
        cellPadding: 2, 
        minCellHeight: 14 
      },
      ...(isListrado ? { alternateRowStyles: { fillColor: hexToRgb(configData?.cor_linha || "#e6ebff") } } : {}),
      margin: { left: margin, right: margin },
    })

    let finalY = doc.lastAutoTable.finalY + 15

    const disciplinasProfTurmas = []
    const mapa = new Map()
    horariosP.forEach((h) => {
      if (h.disciplina?.nome && h.turma?.nome) {
        const chave = `${h.disciplina.nome}-${h.turma.nome}`
        if (!mapa.has(chave)) {
          mapa.set(chave, {
            disciplina: h.disciplina.nome,
            tipo: h.disciplina.tipo || "G",
            turma: h.turma.nome,
          })
        }
      }
    })
    disciplinasProfTurmas.push(...Array.from(mapa.values()))

    if (disciplinasProfTurmas.length > 0) {
      autoTable(doc, {
        startY: finalY,
        head: [
          [
            { content: "DISCIPLINAS", styles: { halign: "left", fontSize: 7 } },
            { content: "TURMA", styles: { halign: "left", fontSize: 7 } },
          ],
        ],
        body: disciplinasProfTurmas.map((item) => [
          { content: `${item.disciplina} (${item.tipo})`, styles: { fontSize: 6 } },
          { content: item.turma, styles: { fontSize: 6 } },
        ]),
        theme: "grid",
        headStyles: { 
          fillColor: hexToRgb(configData?.cor_cabecalho || "#1e3a5f"), 
          textColor: hexToRgb(configData?.cor_texto || "#ffffff"), 
          fontSize: 7 
        },
        bodyStyles: { fontSize: 6, textColor: hexToRgb(configData?.cor_texto || "#000000") },
        margin: { left: margin, right: margin },
      })
      finalY = doc.lastAutoTable.finalY + 8
    }

    doc.setFont("helvetica", "normal")
    doc.setFontSize(7)
    doc.text("Legenda: (N) Nuclear (C) Complementar (G) Gerais", margin, finalY)

    finalY += 15
    doc.setFont("helvetica", "italic")
    doc.setFontSize(8)
    doc.setTextColor(100, 100, 100)
    doc.text("CAJÚ TIMEPLANER | copyright 2026", pageWidth / 2, pageHeight - 20, { align: "center" })
  }

  const exportarPDFTurmas = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" })
    const turmasComHorarios = todasTurmas.filter((t) => todosHorarios.some((h) => h.turma?.id === t.id))

    if (turmasComHorarios.length === 0) {
      alert("Nenhuma turma com horários")
      return
    }

    turmasComHorarios.forEach((turma, idx) => {
      const horariosT = todosHorarios.filter((h) => h.turma?.id === turma.id)
      gerarPDFTurma(doc, turma, horariosT, config, idx === 0)
    })

    doc.save("horarios_turmas.pdf")
  }

  const exportarPDFProfessores = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" })
    const professoresComHorarios = todosProfessores.filter((p) => todosHorarios.some((h) => h.professor?.id === p.id))

    if (professoresComHorarios.length === 0) {
      alert("Nenhum professor com horários")
      return
    }

    professoresComHorarios.forEach((prof, idx) => {
      const horariosP = todosHorarios.filter((h) => h.professor?.id === prof.id)
      gerarPDFProfessor(doc, prof, horariosP, config, idx === 0)
    })

    doc.save("horarios_professores.pdf")
  }

  const exportarExcel = () => {
    const { grade, temposUnicos } = organizarHorariosPorGrade()

    const cabecalho = []
    if (config?.cabecalho_linha1) cabecalho.push([config.cabecalho_linha1])
    if (config?.cabecalho_linha2) cabecalho.push([config.cabecalho_linha2])
    if (config?.nome_instituicao) cabecalho.push([config.nome_instituicao])
    cabecalho.push([])
    cabecalho.push([`HORÁRIO DOCENTE - ${config?.semestre_atual || "I SEMESTRE 2025/2026"}`])
    cabecalho.push([])
    cabecalho.push(["HORAS", "TEMPOS", ...DIAS_SEMANA_ORDEM.map((d) => DIAS_SEMANA_NOMES[d])])

    const dados = temposUnicos.map((tempo, idx) => {
      const horarioRef = horarios.find((h) => h.hora_inicio === tempo)
      return [
        `${tempo} - ${horarioRef?.hora_fim || ""}`,
        `${idx + 1}º`,
        ...DIAS_SEMANA_ORDEM.map((dia) => grade[tempo][dia]?.disciplina?.nome || "-"),
      ]
    })

    const ws = XLSX.utils.aoa_to_sheet([...cabecalho, ...dados])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Horários")
    XLSX.writeFile(wb, "horarios.xlsx")
  }

  const exportarWord = () => {
    const { grade, temposUnicos } = organizarHorariosPorGrade()
    const nomeArquivo = turmaId ? (turmaInfo?.nome || "turma") : professorId ? `professor_${professorId}` : "horario"
    const cabecalho = turmaId ? `Horário da Turma: ${turmaInfo?.nome || ""}` : professorId ? `Horário do Professor` : "Horários"

    let html = `<!DOCTYPE html><html><head><meta charset='UTF-8'><title>${cabecalho}</title>`
    html += `<style>table{border-collapse:collapse;width:100%;}td,th{border:1px solid #333;padding:6px;font-size:12px;}th{background:#1e3a5f;color:#fff;}</style></head><body>`
    html += `<h2>${cabecalho}</h2>`
    if (turmaInfo) {
      html += `<p><strong>Turma:</strong> ${turmaInfo.nome || "-"}`
      if (turmaInfo.sala?.nome) html += ` | <strong>Sala:</strong> ${turmaInfo.sala.nome}`
      if (turmaInfo.curso?.nome) html += ` | <strong>Curso:</strong> ${turmaInfo.curso.nome}`
      html += `</p>`
    }

    html += `<table><thead><tr><th>HORAS</th><th>TEMPOS</th>`
    DIAS_SEMANA_ORDEM.forEach((dia) => {
      html += `<th>${DIAS_SEMANA_NOMES[dia]}</th>`
    })
    html += `</tr></thead><tbody>`

    temposUnicos.forEach((tempo, idx) => {
      const horarioRef = horarios.find((h) => h.hora_inicio === tempo)
      html += `<tr>`
      html += `<td>${tempo} - ${horarioRef?.hora_fim || ""}</td>`
      html += `<td>${idx + 1}º</td>`
      DIAS_SEMANA_ORDEM.forEach((dia) => {
        const h = grade[tempo][dia]
        html += `<td>${h ? `${h.disciplina?.nome || "-"}${h.turma?.nome ? `<br/><small>${h.turma.nome}</small>` : ""}` : "-"}</td>`
      })
      html += `</tr>`
    })

    html += `</tbody></table></body></html>`

    const blob = new Blob(["\ufeff", html], { type: "application/msword" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `${nomeArquivo.replace(/\s+/g, "_").toLowerCase()}.doc`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const { grade, temposUnicos } = organizarHorariosPorGrade()
  const disciplinasProfs = getDisciplinasComProfessores()

  // Gerar estilos dinâmicos com as cores configuradas
  const dinamicoStyles = config ? `
    :root {
      --cor-cabecalho: ${config.cor_cabecalho || '#1e3a5f'};
      --cor-corpo: ${config.cor_corpo || '#f0f5ff'};
      --cor-texto: ${config.cor_texto || '#000000'};
    }
    .grade-table th {
      background: linear-gradient(135deg, var(--cor-cabecalho), var(--cor-cabecalho)) !important;
    }
    .grade-table td {
      color: var(--cor-texto) !important;
    }
    .grade-table tbody tr:nth-child(even) td {
      background-color: var(--cor-corpo) !important;
    }
  ` : ``

  return (
    <div className="horario-container">
      {dinamicoStyles && <style dangerouslySetInnerHTML={{ __html: dinamicoStyles }} />}
      {config?.nome_instituicao && (
        <div className="instituicao-header-display">
          {config.cabecalho_linha1 && <p className="cabecalho-linha">{config.cabecalho_linha1}</p>}
          {config.cabecalho_linha2 && <p className="cabecalho-linha">{config.cabecalho_linha2}</p>}
          <h2 className="nome-instituicao">{config.nome_instituicao}</h2>
          {config.cabecalho_linha3 && <p className="cabecalho-linha">{config.cabecalho_linha3}</p>}
          {config.cabecalho_linha4 && <p className="cabecalho-linha">{config.cabecalho_linha4}</p>}
        </div>
      )}

      {professorInfo && (
        <div className="professor-header-display">
          <h3>Horário do Professor {professorInfo.nome}</h3>
        </div>
      )}

      <div className="export-buttons">
        <button className="btn-export pdf" onClick={exportarPDFTurmas}>
          PDF Turmas
        </button>
        <button className="btn-export pdf-prof" onClick={exportarPDFProfessores}>
          PDF Professores
        </button>
        <button className="btn-export excel" onClick={exportarExcel}>
          Excel
        </button>
        <button className="btn-export word" onClick={exportarWord}>
          Word
        </button>
      </div>

      {turmaInfo && (
        <div className="turma-info-header">
          <span>
            <strong>Turma:</strong> {turmaInfo.nome}
          </span>
          {turmaInfo.sala?.nome && (
            <span>
              <strong>Sala:</strong> {turmaInfo.sala.nome}
            </span>
          )}
          {turmaInfo.curso?.nome && (
            <span>
              <strong>Curso:</strong> {turmaInfo.curso.nome}
            </span>
          )}
          {turmaInfo.ano && (
            <span>
              <strong>Ano:</strong> {turmaInfo.ano}º
            </span>
          )}
        </div>
      )}

      <div className="table-wrapper" ref={tableRef}>
        <table className="grade-table">
          <thead>
            <tr>
              <th className="th-hora">HORAS</th>
              <th className="th-tempo">TEMPOS</th>
              {DIAS_SEMANA_ORDEM.map((dia) => (
                <th key={dia} className="th-dia">
                  {DIAS_SEMANA_NOMES[dia]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {temposUnicos.length === 0 ? (
              <tr>
                <td colSpan={7} className="sem-horarios">
                  Nenhum horário cadastrado
                </td>
              </tr>
            ) : (
              temposUnicos.map((tempo, idx) => {
                const horarioRef = horarios.find((h) => h.hora_inicio === tempo)
                return (
                  <tr key={tempo}>
                    <td className="td-hora">
                      {tempo} - {horarioRef?.hora_fim || ""}
                    </td>
                    <td className="td-tempo">{idx + 1}º</td>
                    {DIAS_SEMANA_ORDEM.map((dia) => {
                      const h = grade[tempo][dia]
                      return (
                        <td key={dia} className={`td-disciplina ${h ? "" : "vazio"}`}>
                          {h ? (
                            <>
                              <div>{h.disciplina?.nome || "-"}</div>
                              {h.turma?.nome && <div className="turma-info-cell">{h.turma.nome}</div>}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {disciplinasProfs.length > 0 && (
        <div className="legenda-container">
          <table className="legenda-table">
            <thead>
              <tr>
                <th>DISCIPLINAS</th>
                <th>NOME DO DOCENTE</th>
              </tr>
            </thead>
            <tbody>
              {disciplinasProfs.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    {item.disciplina} ({item.tipo})
                  </td>
                  <td>{item.professor}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="legenda-tipos">Legenda: (N) Nuclear (C) Complementar (G) Gerais</p>
        </div>
      )}
    </div>
  )
}
